import {messageType} from '../../../../../types/message'
import userIcon from '../../../../../assets/guru.png'
import schiefIcon from '../../../../../assets/schiefIcon.svg'
import pdf from '../../../../../assets/pdf.png'
import docx from '../../../../../assets/docx.png'
import styled from 'styled-components'
import {getCurrentColors} from '../../../../../utils/colors'
import {useEffect, useMemo, useState} from 'react'
import Markdown, {type Components} from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Tools from './tools'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

interface MessageProps {
    message: messageType
    arrayLength: number
    index: number
    loadingMessageBot: boolean
}

const COLORS = getCurrentColors() // Importando as cores do tema atual

const MessageContainer = styled.section<{isUser: boolean}>`
    display: flex;
    align-items: flex-start;
    margin: 20px 0;
    border-radius: 20px;
    background-color: ${({isUser}) => (isUser ? '#ffffff' : COLORS.secundaria)};
    padding: 15px;
    width: fit-content;
    max-width: 100%;
    box-sizing: border-box;
    align-self: ${({isUser}) => (isUser ? 'flex-end' : 'flex-start')};
`

const Identity = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    margin-bottom: 15px;

    & p {
        font-size: 12px;
        font-weight: 700;
        color: ${COLORS.cinzaEscuro};
    }

    & img {
        width: 44px;
        height: 44px;
    }
`

const MarkdownContent = styled.div<{isUser: boolean}>`
    font-size: 16px;
    color: ${({isUser}) => (isUser ? '#000000' : '#000000')};
    word-wrap: break-word;
    line-height: 1.6;
    text-align: ${({isUser}) => (isUser ? 'right' : 'left')};
    max-width: 100%;
    min-width: 0;

    p {
        margin-bottom: 10px;
    }

    ul {
        margin-left: 20px;
    }

    h1,
    h2,
    h3,
    h4 {
        margin-top: 5px;
        margin-bottom: 20px;
    }

    table {
        width: max-content;
        min-width: 100%;
        border-collapse: collapse;
        margin: 0;
        color: ${COLORS.cinzaEscuro};
        background-color: #77dccb;
        font-size: 14px;
    }

    th,
    td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
        max-width: 200px;
        width: 200px;
        white-space:;
        word-break: break-word;
    }

    th {
        background-color: #f4f4f4;
    }

    tr:nth-child(even) {
        background-color: #f9f9f9;
    }
`

const TableScrollWrapper = styled.div`
    width: fit-content;
    max-width: clamp(260px, 78vw, 720px);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 20px 0;

    @media (max-width: 640px) {
        max-width: calc(100vw - 48px);
    }
`

const DocumentsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
    align-self: flex-end;
`

const MessageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`

const MessageStatus = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    font-size: 12px;
    color: #9E9E9E;
    justify-content: flex-end;
`

const loadingMessages = [
    'Consultando banco de dados',
    'Filtrando documentos',
    'Gerando resposta com IA',
]

const LoadingBubble = styled.div`
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    color: #000000;
`

export default function Message({
    arrayLength,
    message,
    index,
    loadingMessageBot,
}: MessageProps) {
    const isUser = !!message.author_id
    const isLastMessage = arrayLength - 1 === index
    const showLoadingMessage = loadingMessageBot && isUser && isLastMessage
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
    const markdownComponents = useMemo<Components>(
        () => ({
            table: ({node: _node, ...props}) => (
                <TableScrollWrapper>
                    <table {...props} />
                </TableScrollWrapper>
            ),
        }),
        []
    )

    useEffect(() => {
        if (!showLoadingMessage) {
            setLoadingMessageIndex(0)
            return
        }

        const intervalId = window.setInterval(() => {
            setLoadingMessageIndex(
                (currentIndex) => (currentIndex + 1) % loadingMessages.length
            )
        }, 1500)

        return () => window.clearInterval(intervalId)
    }, [showLoadingMessage])

    const getFileIconByName = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase()

        if (extension === 'pdf') {
            return pdf
        }

        if (extension === 'docx') {
            return docx
        }

        return pdf
    }

    const messageTime = (() => {
        const createdAtRaw = String(message.created_at)
        const createdAtValue = createdAtRaw.match(/(z|[+-]\d{2}:?\d{2})$/i)
            ? createdAtRaw
            : `${createdAtRaw}Z`
        const createdAt = new Date(createdAtValue)
        if (Number.isNaN(createdAt.getTime())) {
            return ''
        }

        return createdAt.toLocaleTimeString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })
    })()

    return (
        <MessageWrapper>
            <MessageContainer isUser={isUser}>
                {!isUser && (
                    <Identity>
                        <img
                            src={
                                window.location.hostname.includes('procon')
                                    ? schiefIcon
                                    : userIcon
                            }
                            alt="Ícone de usuário"
                        />
                    </Identity>
                )}
                <MarkdownContent isUser={isUser}>
                    <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                    >
                        {message.response || message.content}
                    </Markdown>
                    {!isUser && (
                        <Tools
                            lastAssistantMessage={isLastMessage}
                            message={message}
                        />
                    )}
                    {isUser && messageTime && (
                        <MessageStatus>
                            <span>Enviado</span>
                            <AccessTimeIcon
                                fontSize="inherit"
                                sx={{color: '#9E9E9E'}}
                            />
                            <span>{messageTime}</span>
                        </MessageStatus>
                    )}
                </MarkdownContent>
            </MessageContainer>
            {showLoadingMessage && (
                <MessageContainer isUser={false} aria-live="polite">
                    <Identity>
                        <img
                            src={
                                window.location.hostname.includes('procon')
                                    ? schiefIcon
                                    : userIcon
                            }
                            alt="Ícone de assistente"
                        />
                    </Identity>
                    <MarkdownContent isUser={false}>
                        <LoadingBubble>
                            {loadingMessages[loadingMessageIndex]}
                        </LoadingBubble>
                    </MarkdownContent>
                </MessageContainer>
            )}
            {isUser && (
                <DocumentsContainer>
                    {message.uploaded_documents.map((file, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#FFFFFF',
                                padding: '10px',
                                borderRadius: '16px',
                                width: 'fit-content',
                                cursor: 'pointer',
                            }}
                        >
                            <img
                                src={getFileIconByName(file)}
                                alt="Arquivo"
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    marginRight: '5px',
                                }}
                            />
                            <span
                                style={{
                                    color: '#000000',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {file.split('/').pop()}
                            </span>
                        </div>
                    ))}
                </DocumentsContainer>
            )}
        </MessageWrapper>
    )
}
